import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Ban, ImagePlus, Shapes, Trash2, X } from "lucide-react";
import { BookingPageServiceContract } from "../../features/trial-requests/contracts/BookingPageServiceContract";
import {
  BOOKING_PAGE_SERVICE_ICONS,
  isBookingPageServiceIcon,
} from "../../features/trial-requests/enums/BookingPageServiceIcons";
import { SERVICE_ICON_MAP } from "./serviceIconMap";

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_BYTES = 8 * 1024 * 1024;

interface ServiceEditorRowProps {
  service: BookingPageServiceContract;
  onChange: (patch: Partial<BookingPageServiceContract>) => void;
  onRemove: () => void;
  onUploadPicture: (file: File) => void;
  uploading: boolean;
}

/**
 * One editable service row: texts, an optional curated icon, and an optional
 * uploaded picture (which replaces the icon on the public page).
 */
const ServiceEditorRow = ({ service, onChange, onRemove, onUploadPicture, uploading }: ServiceEditorRowProps) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const IconPreview =
    service.icon && isBookingPageServiceIcon(service.icon) ? SERVICE_ICON_MAP[service.icon] : null;

  return (
    <div className="mt-4 rounded-xl border border-grey-90 p-3 dark:border-grey-50">
      <div className="flex items-start gap-2">
        <input
          className="input flex-1"
          value={service.title}
          maxLength={80}
          onChange={(event) => onChange({ title: event.target.value })}
          placeholder={t("website.serviceTitlePlaceholder", "e.g. Trial lesson")}
        />
        <button
          type="button"
          className="btn btn-secondary shrink-0"
          onClick={onRemove}
          aria-label={t("website.removeService", "Remove service")}
        >
          <Trash2 size={16} />
        </button>
      </div>
      <input
        className="input mt-2"
        value={service.description ?? ""}
        maxLength={300}
        onChange={(event) => onChange({ description: event.target.value })}
        placeholder={t("website.serviceDescriptionPlaceholder", "Short description (optional)")}
      />
      <input
        className="input mt-2"
        value={service.priceText ?? ""}
        maxLength={50}
        onChange={(event) => onChange({ priceText: event.target.value })}
        placeholder={t("website.servicePricePlaceholder", "e.g. 60 min · 45 € (optional)")}
      />

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {service.imageUrl ? (
          <>
            <img
              src={service.imageUrl}
              alt={service.title}
              className="h-10 w-10 rounded-lg object-cover"
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => onChange({ imageUrl: null })}
            >
              <X size={16} />
              {t("website.removePicture", "Remove picture")}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setPickerOpen((open) => !open)}
              aria-label={t("website.serviceIconLabel", "Icon")}
              aria-expanded={pickerOpen}
            >
              {IconPreview ? <IconPreview size={18} /> : <Shapes size={18} />}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_TYPES.join(",")}
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (!file) return;
                if (!ACCEPTED_TYPES.includes(file.type) || file.size > MAX_BYTES) {
                  alert(t("website.imageInvalid", "Please choose a JPEG, PNG or WebP image up to 8 MB."));
                  return;
                }
                onUploadPicture(file);
              }}
            />
            <button
              type="button"
              className="btn btn-secondary"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              <ImagePlus size={16} />
              {t("website.uploadPicture", "Own picture")}
            </button>
          </>
        )}
      </div>

      {pickerOpen && !service.imageUrl && (
        <div className="mt-2 flex flex-wrap gap-1 rounded-xl bg-grey-95 p-2 dark:bg-primary-20">
          <button
            type="button"
            className={
              service.icon
                ? "rounded-lg p-2 text-grey-40 hover:bg-white dark:text-grey-80 dark:hover:bg-primary-10"
                : "rounded-lg bg-white p-2 text-primary-40 dark:bg-primary-10 dark:text-primary-90"
            }
            onClick={() => {
              onChange({ icon: null });
              setPickerOpen(false);
            }}
            aria-label={t("website.noIcon", "No icon")}
          >
            <Ban size={18} />
          </button>
          {BOOKING_PAGE_SERVICE_ICONS.map((icon) => {
            const Icon = SERVICE_ICON_MAP[icon];
            return (
              <button
                key={icon}
                type="button"
                className={
                  service.icon === icon
                    ? "rounded-lg bg-white p-2 text-primary-40 dark:bg-primary-10 dark:text-primary-90"
                    : "rounded-lg p-2 text-grey-30 hover:bg-white dark:text-grey-70 dark:hover:bg-primary-10"
                }
                onClick={() => {
                  onChange({ icon });
                  setPickerOpen(false);
                }}
                aria-label={icon}
              >
                <Icon size={18} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ServiceEditorRow;
